import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout, { PageHeader } from "@/components/Layout";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, GitBranch, ArrowRight, Trash, GearSix, Play, Clock } from "@phosphor-icons/react";

const INTERVALS = [
  { value: "60", label: "Hourly" },
  { value: "360", label: "Every 6 hours" },
  { value: "1440", label: "Daily" },
  { value: "10080", label: "Weekly" },
];

export default function Projects() {
  const [projects, setProjects] = useState([]);
  const [scheduleFor, setScheduleFor] = useState(null);
  const [schedEnabled, setSchedEnabled] = useState(false);
  const [schedInterval, setSchedInterval] = useState("1440");
  const [schedSaving, setSchedSaving] = useState(false);
  const navigate = useNavigate();

  const load = () => api.get("/projects").then((r) => setProjects(r.data));
  useEffect(() => { load(); }, []);

  const openSchedule = (p) => {
    const s = p.schedule || {};
    setSchedEnabled(!!s.enabled);
    setSchedInterval(String(s.interval_minutes || 1440));
    setScheduleFor(p);
  };

  const saveSchedule = async () => {
    setSchedSaving(true);
    try {
      await api.patch(`/projects/${scheduleFor.id}/schedule`, {
        enabled: schedEnabled, interval_minutes: parseInt(schedInterval, 10),
      });
      toast.success(schedEnabled ? "Scheduled sync enabled" : "Scheduled sync disabled");
      setScheduleFor(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save schedule"); }
    finally { setSchedSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this project and its mappings?")) return;
    await api.delete(`/projects/${id}`); toast.success("Project deleted"); load();
  };

  const runFull = async (id) => {
    try {
      const { data } = await api.post("/jobs", { project_id: id, mode: "full" });
      toast.success("Full migration started"); navigate(`/jobs/${data.id}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to start"); }
  };

  return (
    <Layout>
      <PageHeader title="Migration Projects" subtitle="Guided source → destination migrations"
        actions={
          <Button data-testid="new-project-btn" onClick={() => navigate("/projects/new")}
            className="rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200 gap-2">
            <Plus size={16} /> New Migration Project
          </Button>
        }
      />
      <div className="p-8">
        {projects.length === 0 ? (
          <div className="bg-white border border-dashed border-stone-300 rounded-xl py-20 text-center">
            <GitBranch size={40} className="mx-auto text-stone-300" />
            <p className="text-stone-500 mt-4 text-sm">No migration projects yet.</p>
          </div>
        ) : (
          <div className="bg-white border border-stone-200 rounded-2xl shadow-card overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-stone-500 border-b border-stone-200">
                  <th className="px-6 py-3 font-semibold">Project</th>
                  <th className="px-6 py-3 font-semibold">Pipeline</th>
                  <th className="px-6 py-3 font-semibold">Status</th>
                  <th className="px-6 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {projects.map((p) => (
                  <tr key={p.id} data-testid={`project-row-${p.id}`} className="border-b border-stone-100 hover:bg-stone-50 transition-colors duration-200">
                    <td className="px-6 py-4 font-medium text-stone-900">{p.name}</td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center gap-2 font-mono text-xs">
                        <span className="px-2 py-0.5 bg-stone-100 rounded-xl">{p.source_name}</span>
                        <ArrowRight size={14} className="text-[#C2410C]" />
                        <span className="px-2 py-0.5 bg-stone-100 rounded-xl">{p.target_name}</span>
                      </span>
                    </td>
                    <td className="px-6 py-4"><StatusBadge status={p.status} /></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-end gap-2">
                        <Button data-testid={`configure-${p.id}`} size="sm" variant="outline"
                          onClick={() => navigate(`/projects/${p.id}/wizard`)} className="rounded-xl gap-1.5 text-xs">
                          <GearSix size={14} /> Configure
                        </Button>
                        <Button data-testid={`schedule-${p.id}`} size="sm" variant="outline"
                          onClick={() => openSchedule(p)}
                          className={`rounded-xl gap-1.5 text-xs ${p.schedule?.enabled ? "border-[#C2410C] text-[#C2410C]" : ""}`}>
                          <Clock size={14} /> {p.schedule?.enabled ? "Scheduled" : "Schedule"}
                        </Button>
                        <Button data-testid={`run-${p.id}`} size="sm"
                          onClick={() => runFull(p.id)} className="rounded-xl gap-1.5 text-xs bg-[#C2410C] hover:bg-[#C2410C] transition-colors duration-200">
                          <Play size={14} /> Run
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => del(p.id)}
                          className="rounded-xl text-rose-600 hover:bg-rose-50">
                          <Trash size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={!!scheduleFor} onOpenChange={(o) => !o && setScheduleFor(null)}>
        <DialogContent className="rounded-xl" data-testid="schedule-dialog">
          <DialogHeader>
            <DialogTitle className="font-head">Scheduled Sync</DialogTitle>
          </DialogHeader>
          <div className="space-y-5 py-2">
            <p className="text-sm text-stone-500">
              Automatically re-run this migration on a recurring schedule. Only new/changed
              records are created (idempotent), so existing records are skipped.
            </p>
            <div className="flex items-center justify-between">
              <Label htmlFor="sched-toggle" className="text-sm">Enable scheduled sync</Label>
              <Switch id="sched-toggle" data-testid="schedule-enabled-switch"
                checked={schedEnabled} onCheckedChange={setSchedEnabled} />
            </div>
            <div className="space-y-2">
              <Label className="text-sm">Frequency</Label>
              <Select value={schedInterval} onValueChange={setSchedInterval} disabled={!schedEnabled}>
                <SelectTrigger data-testid="schedule-interval" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {INTERVALS.map((i) => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-xl" onClick={() => setScheduleFor(null)}>Cancel</Button>
            <Button data-testid="schedule-save-btn" onClick={saveSchedule} disabled={schedSaving}
              className="rounded-xl bg-[#C2410C] hover:bg-[#9A3412] transition-colors duration-200">
              {schedSaving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
